import { BrandKit } from '@dtx-company/app/shared/code/src/components/PrintStoreTemplates/BrandKit/BrandKitForm/BrandKitForm.types'
import { Category } from '@dtx-company/flow-codegen/src/code/generated.types'
import {
  DHElementsData,
  DHProjectGetRenderResult
} from '@dtx-company/app/shared/code/src/types/design-huddle.types'
import { FlowcodeType } from '../../../types/flowcode'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { getIdFromEncodedString } from '@dtx-company/true-common/src/utils/strings'

export enum TemplateFilterSection {
  Vertical = 'Vertical',
  UseCase = 'Use Case'
}

export interface TemplateFiltersState {
  selectedCategories: {
    verticals: Category[]
    useCases: Category[]
  }
}

export interface PrintStoreSliceState extends TemplateFiltersState {
  selectedFlowcode: FlowcodeType | undefined
  selectedTemplateFlowcode: FlowcodeType | undefined
  batchId: string | undefined
  templateBatchId: string | undefined
  nickname: string | undefined
  templateFlowcodeNickname: string | undefined
  templateId: string | undefined
  dhTemplateId: string | undefined
  templateCategoryId: string | undefined
  productId: string | undefined
  brandKit: BrandKit
  templateIdToDhProjectMap: Record<string, string>
  dhProject?: DHElementsData
  dhProjectImage?: DHProjectGetRenderResult
}

export interface HydrateFromUrlProps {
  batchId?: PrintStoreSliceState['batchId']
  dhTemplateId?: PrintStoreSliceState['dhTemplateId']
  templateId?: PrintStoreSliceState['templateId']
  templateCategoryId?: PrintStoreSliceState['templateCategoryId']
  productId?: PrintStoreSliceState['productId']
  verticals?: Category[]
  useCases?: Category[]
}

export const TemplateFilterSectionsMapping: Record<
  TemplateFilterSection,
  keyof TemplateFiltersState['selectedCategories']
> = {
  [TemplateFilterSection.Vertical]: 'verticals',
  [TemplateFilterSection.UseCase]: 'useCases'
}

export const getTemplateFiltersKey = (
  sectionName: TemplateFilterSection
): keyof TemplateFiltersState['selectedCategories'] => {
  return TemplateFilterSectionsMapping[sectionName]
}

const initialPrintStoreState: PrintStoreSliceState = {
  selectedFlowcode: undefined,
  selectedTemplateFlowcode: undefined,
  batchId: undefined,
  nickname: undefined,
  templateFlowcodeNickname: undefined,
  templateBatchId: undefined,
  templateId: undefined,
  dhTemplateId: undefined,
  templateCategoryId: undefined,
  productId: undefined,
  brandKit: {
    editorFlowcodeImage: undefined,
    highlightedTemplatesFlowcodeImage: undefined,
    templatesListFlowcodeImage: undefined,
    primaryText: undefined,
    primaryColor: undefined,
    secondaryColor: undefined,
    logoImage: undefined
  },
  templateIdToDhProjectMap: {},
  selectedCategories: {
    verticals: [],
    useCases: []
  }
}

const printStoreReduxSlice = createSlice({
  name: 'printStoreSelections',
  initialState: initialPrintStoreState,
  reducers: {
    setSelectedTemplateFlowcode: (state, action: PayloadAction<FlowcodeType | undefined>) => {
      state.selectedTemplateFlowcode = action.payload
      state.templateBatchId = action.payload ? getIdFromEncodedString(action.payload.id) : undefined
      state.templateFlowcodeNickname = action.payload?.nickname
    },
    setSelectedFlowcode: (state, action: PayloadAction<FlowcodeType | undefined>) => {
      state.selectedFlowcode = action.payload
      state.batchId = action.payload ? getIdFromEncodedString(action.payload.id) : undefined
      state.nickname = action.payload?.nickname
    },
    setBatchId: (state, action: PayloadAction<string | undefined>) => {
      state.batchId = action.payload
    },
    setNickname: (state, action: PayloadAction<string>) => {
      state.nickname = action.payload
    },
    hydrateFromUrl: (state, action: PayloadAction<HydrateFromUrlProps>) => {
      const {
        batchId,
        dhTemplateId,
        templateId,
        templateCategoryId,
        productId,
        verticals,
        useCases
      } = action.payload
      if (batchId) {
        state.batchId = batchId
      }
      if (dhTemplateId) {
        state.dhTemplateId = dhTemplateId
      }
      if (templateId) {
        state.templateId = templateId
      }
      if (templateCategoryId) {
        state.templateCategoryId = templateCategoryId
      }
      if (productId) {
        state.productId = productId
      }
      if (verticals) {
        state.selectedCategories.verticals = verticals
      }
      if (useCases) {
        state.selectedCategories.useCases = useCases
      }
    },
    setTemplateId: (state, action: PayloadAction<string | undefined>) => {
      state.templateId = action.payload
    },
    setTemplateCategoryId: (state, action: PayloadAction<string | undefined>) => {
      state.templateCategoryId = action.payload
    },
    setProductId: (state, action: PayloadAction<string | undefined>) => {
      state.productId = action.payload
    },
    setDhProjectIdForTemplate: (
      state,
      action: PayloadAction<{ dhProjectId: string; dhTemplateId: string }>
    ) => {
      const { dhProjectId, dhTemplateId } = action.payload
      state.templateIdToDhProjectMap[dhTemplateId] = dhProjectId
    },
    setBrandKit: (state, action: PayloadAction<BrandKit>) => {
      state.brandKit = action.payload
    },
    setDhProject: (state, action: PayloadAction<DHElementsData>) => {
      state.dhProject = action.payload
    },
    setDhProjectImage: (state, action: PayloadAction<DHProjectGetRenderResult>) => {
      state.dhProjectImage = action.payload
    },
    addSelectedCategory: (state, action: PayloadAction<Category>) => {
      const templateFiltersKey = getTemplateFiltersKey(
        action.payload.sectionName as TemplateFilterSection
      )
      state.selectedCategories[templateFiltersKey] = [
        ...state.selectedCategories[templateFiltersKey],
        action.payload
      ]
    },
    removeSelectedCategory: (state, action: PayloadAction<Category>) => {
      const templateFiltersKey = getTemplateFiltersKey(
        action.payload.sectionName as TemplateFilterSection
      )
      state.selectedCategories[templateFiltersKey] = state.selectedCategories[
        templateFiltersKey
      ].filter(category => category.categoryId !== action.payload.categoryId)
    },
    resetSelectedCategories: (state, action: PayloadAction<Category[]>) => {
      const sectionName = action?.payload?.[0]?.sectionName
      const templateFiltersKey = sectionName
        ? getTemplateFiltersKey(sectionName as TemplateFilterSection)
        : undefined
      if (templateFiltersKey) {
        state.selectedCategories[templateFiltersKey] = []
      }
    },
    resetPrintStoreSlice: state => {
      state = initialPrintStoreState
    }
  }
})

export const {
  setSelectedFlowcode,
  setSelectedTemplateFlowcode,
  setBatchId,
  setNickname,
  setTemplateId,
  setTemplateCategoryId,
  setProductId,
  setDhProjectIdForTemplate,
  setBrandKit,
  hydrateFromUrl,
  setDhProject,
  setDhProjectImage,
  resetPrintStoreSlice,
  addSelectedCategory,
  removeSelectedCategory,
  resetSelectedCategories
} = printStoreReduxSlice.actions

export default printStoreReduxSlice.reducer
